import { useState, useEffect, useCallback } from 'react';
import {
  Globe,
  Play,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  ArrowRight,
  BookOpen,
} from 'lucide-react';
import clsx from 'clsx';
import { periodDisplayName, type GaapConversion, type Period } from '@/types';
import { multiGaapApi, periodsApi } from '@/api/client';
import { useAppStore } from '@/store';

// --- Helpers -----------------------------------------------------------------

function formatAmount(amount: number): string {
  if (amount === 0) return '\u2014';
  const abs = Math.abs(amount);
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(abs);
  return amount < 0 ? `(${formatted})` : formatted;
}

// --- Main Page ---------------------------------------------------------------

export default function MultiGAAP() {
  const { addNotification } = useAppStore();

  const [isLoading, setIsLoading] = useState(true);
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [periods, setPeriods] = useState<Period[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [standards, setStandards] = useState<{ code: string; name: string; description: string }[]>([]);
  const [sourceGaap] = useState('IFRS');
  const [targetGaap, setTargetGaap] = useState('US_GAAP');

  const [conversion, setConversion] = useState<GaapConversion | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [periodsRes, standardsRes] = await Promise.all([
        periodsApi.list(),
        multiGaapApi.standards().catch(() => [
          { code: 'IFRS', name: 'IFRS', description: 'International Financial Reporting Standards' },
          { code: 'US_GAAP', name: 'US GAAP', description: 'United States Generally Accepted Accounting Principles' },
          { code: 'UAE_LOCAL', name: 'UAE Local GAAP', description: 'UAE Local Accounting Standards' },
        ]),
      ]);
      setPeriods(periodsRes);
      setStandards(standardsRes);
      if (periodsRes.length > 0 && !selectedPeriod) {
        const open = periodsRes.find((p) => p.status === 'open') || periodsRes[0];
        setSelectedPeriod(open.id);
      }
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setError(apiErr.detail || apiErr.message || 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleConvert = async () => {
    if (!selectedPeriod) return;
    setConverting(true);
    setConversion(null);
    try {
      const result = await multiGaapApi.convert({
        period_id: selectedPeriod,
        source_gaap: sourceGaap,
        target_gaap: targetGaap,
      });
      setConversion(result);
      addNotification({ type: 'success', title: 'Conversion Complete', message: `GAAP conversion from ${sourceGaap} to ${targetGaap} completed.` });
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Conversion Failed', message: apiErr.detail || 'Failed to convert.' });
    } finally {
      setConverting(false);
    }
  };

  const toggleRow = (index: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const targetStandards = standards.filter((s) => s.code !== sourceGaap);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
            <Globe className="w-5 h-5 text-teal-700" />
          </div>
          <div>
            <div className="skeleton w-56 h-7 mb-1" />
            <div className="skeleton w-72 h-4" />
          </div>
        </div>
        <div className="card p-6 animate-pulse">
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="skeleton w-full h-10" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
          <Globe className="w-5 h-5 text-teal-700" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Multi-GAAP Conversion</h1>
          <p className="text-sm text-gray-500 mt-0.5">Convert financial statements between accounting standards</p>
        </div>
      </div>

      {error && (
        <div className="card p-4 border-red-200 bg-red-50">
          <div className="flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
            <button onClick={loadData} className="ml-auto text-xs font-medium underline">Retry</button>
          </div>
        </div>
      )}

      {/* Conversion Controls */}
      <div className="card p-5">
        <h2 className="text-base font-semibold text-gray-900 mb-4">GAAP Conversion</h2>
        <div className="flex items-end gap-4 flex-wrap">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Period</label>
            <select className="select-field text-sm w-40" value={selectedPeriod} onChange={(e) => setSelectedPeriod(e.target.value)}>
              {periods.map((p) => (
                <option key={p.id} value={p.id}>{periodDisplayName(p)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Source GAAP</label>
            <div className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-teal-600" />
              {sourceGaap}
            </div>
          </div>
          <div className="flex items-center px-2">
            <ArrowRight className="w-5 h-5 text-gray-400" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Target GAAP</label>
            <select className="select-field text-sm w-48" value={targetGaap} onChange={(e) => setTargetGaap(e.target.value)}>
              {targetStandards.map((s) => (
                <option key={s.code} value={s.code}>{s.name}</option>
              ))}
            </select>
          </div>
          <button onClick={handleConvert} disabled={converting || !selectedPeriod} className="btn-primary text-sm">
            {converting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Play className="w-4 h-4 mr-1.5" />}
            Convert
          </button>
        </div>
      </div>

      {/* Results */}
      {converting && (
        <div className="card py-20 text-center">
          <Loader2 className="w-10 h-10 mx-auto text-teal-500 animate-spin mb-3" />
          <p className="text-sm text-gray-500">Converting financial statements...</p>
        </div>
      )}

      {conversion && !converting && (
        <>
          {/* Reconciliation Bridge Table */}
          <div className="card overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50">
              <h2 className="text-base font-semibold text-gray-900">
                Reconciliation Bridge: {conversion.source_gaap} to {conversion.target_gaap}
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="table-header">Line Item</th>
                    <th className="table-header text-right">{conversion.source_gaap} Amount</th>
                    <th className="table-header text-right">Adjustments</th>
                    <th className="table-header text-right">{conversion.target_gaap} Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {conversion.reconciliation.map((item, index) => (
                    <tr key={index} className={clsx(
                      'border-b border-gray-100 hover:bg-gray-50/50 transition-colors',
                      item.adjustments !== 0 && 'font-medium',
                    )}>
                      <td className="table-cell text-sm text-gray-900">{item.line_item}</td>
                      <td className="table-cell text-right font-mono text-sm text-gray-700">
                        {formatAmount(item.source_amount)}
                      </td>
                      <td className="table-cell text-right font-mono text-sm">
                        <span className={clsx(
                          item.adjustments > 0 ? 'text-green-700' : item.adjustments < 0 ? 'text-red-700' : 'text-gray-400',
                        )}>
                          {item.adjustments !== 0 ? (item.adjustments > 0 ? '+' : '') + formatAmount(item.adjustments) : '\u2014'}
                        </span>
                      </td>
                      <td className="table-cell text-right font-mono text-sm font-medium text-gray-900">
                        {formatAmount(item.target_amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Adjustment Details */}
          <div className="card overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50">
              <h2 className="text-base font-semibold text-gray-900">Adjustment Details</h2>
            </div>
            {conversion.adjustments.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-400">No adjustments required</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {conversion.adjustments.map((adj, index) => (
                  <div key={index} className="hover:bg-gray-50/50 transition-colors">
                    <button
                      onClick={() => toggleRow(index)}
                      className="w-full flex items-center justify-between px-6 py-3 text-left"
                    >
                      <div className="flex items-center gap-3">
                        {expandedRows.has(index) ? (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        )}
                        <div>
                          <p className="text-sm font-medium text-gray-900">{adj.rule}</p>
                          <p className="text-xs text-gray-500">{adj.account}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="badge bg-teal-50 text-teal-700 text-xs">{adj.standard_ref}</span>
                        <span className={clsx(
                          'font-mono text-sm font-medium',
                          adj.amount > 0 ? 'text-green-700' : adj.amount < 0 ? 'text-red-700' : 'text-gray-700',
                        )}>
                          {adj.amount > 0 ? '+' : ''}{formatAmount(adj.amount)}
                        </span>
                      </div>
                    </button>
                    {expandedRows.has(index) && (
                      <div className="px-6 pb-4 pl-14">
                        <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
                          <p className="mb-2">{adj.description}</p>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span>Standard: <span className="font-medium text-teal-700">{adj.standard_ref}</span></span>
                            <span>Account: <span className="font-mono">{adj.account}</span></span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Side-by-Side Statements */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Source GAAP */}
            <div className="card overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-200 bg-blue-50">
                <h3 className="text-sm font-semibold text-blue-800">{conversion.source_gaap}</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {conversion.reconciliation.map((item, i) => (
                  <div key={i} className="flex items-center justify-between px-5 py-2.5">
                    <span className="text-sm text-gray-700">{item.line_item}</span>
                    <span className="font-mono text-sm font-medium text-gray-900">{formatAmount(item.source_amount)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Target GAAP */}
            <div className="card overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-200 bg-teal-50">
                <h3 className="text-sm font-semibold text-teal-800">{conversion.target_gaap}</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {conversion.reconciliation.map((item, i) => (
                  <div key={i} className="flex items-center justify-between px-5 py-2.5">
                    <span className="text-sm text-gray-700">{item.line_item}</span>
                    <span className={clsx(
                      'font-mono text-sm font-medium',
                      item.adjustments !== 0 ? 'text-teal-800' : 'text-gray-900',
                    )}>
                      {formatAmount(item.target_amount)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {!conversion && !converting && (
        <div className="card py-20 text-center">
          <Globe className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <h3 className="text-base font-medium text-gray-900">Select Standards & Convert</h3>
          <p className="text-sm text-gray-500 mt-1">
            Choose a period and target GAAP standard, then click "Convert" to generate the reconciliation.
          </p>
        </div>
      )}
    </div>
  );
}
